import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Button from '~/components/button';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';
import Input, { MaskTypes } from '~/components/input';
import Loading from '~/components/loading';
import Pagination from '~/components/pagination';
import TableList from '~/components/tableList';
import { useAuth } from '~/hooks/contexts/useAuth';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import { FormField, setFormState } from '~/utils/form';
import { cpfMask, currencyMask } from '~/utils/masks';
import { IListColCampaing } from '~/views/Resource/Campaing/model';

import * as St from './styles';

export interface NegotiationHistoryItem {
  idNegociacao?: string;
  nome: string;
  cpf: string;
  data: string;
  valorOriginal?: number;
  desconto?: number;
  entrada?: number;
  parcelas?: number;
  vencimento: string;
  status: string;
  operador: string;
}

interface Form {
  cpf: FormField;
}

const INITIAL_FORM: Form = {
  cpf: { name: 'cpf', value: '' },
};

const historyHeader = [
  {
    id: 1,
    value: 'Nome completo',
    maxChar: 24,
    textAlign: 'left',
  },
  {
    id: 2,
    value: 'CPF',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 3,
    value: 'Data',
    maxChar: 12,
    textAlign: 'center',
  },
  {
    id: 4,
    value: 'Valor original',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 5,
    value: 'Desconto',
    maxChar: 14,
    textAlign: 'center',
  },
  {
    id: 6,
    value: 'Entrada',
    maxChar: 14,
    textAlign: 'center',
  },
  {
    id: 7,
    value: 'Parcelas',
    maxChar: 10,
    textAlign: 'center',
  },
  {
    id: 8,
    value: 'Vencimento',
    maxChar: 12,
    textAlign: 'center',
  },
  {
    id: 9,
    value: 'Status',
    maxChar: 12,
    textAlign: 'center',
  },
  {
    id: 10,
    value: 'Operador',
    maxChar: 16,
    textAlign: 'center',
  },
  {
    id: 11,
    value: 'Ação',
    maxChar: 8,
    textAlign: 'center',
  },
];

const PAGE_SIZE = 10;

const formatMoney = (value?: number) =>
  value === undefined || value === null ? '-' : `R$ ${currencyMask(value)}`;

const formatDate = (value?: string) => {
  if (!value) return '-';

  const parsed = moment(value, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
  const date = parsed.isValid() ? parsed : moment(value);

  return date.isValid() ? date.format('DD/MM/YYYY') : value;
};

const firstText = (...values: unknown[]) => {
  const found = values.find(
    (value) => value !== undefined && value !== null && value !== '',
  );

  return found === undefined ? '' : String(found);
};

const firstNumber = (...values: unknown[]) => {
  const found = values.find(
    (value) =>
      typeof value === 'number' ||
      (typeof value === 'string' && value !== '' && !Number.isNaN(Number(value))),
  );

  return found === undefined ? undefined : Number(found);
};

const asRecord = (value: unknown) =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const readNegotiations = (payload: unknown) => {
  const record = asRecord(payload);
  const nested = asRecord(record?.data);
  const source = nested ?? record;
  const listKeys = ['content', 'negociacoes', 'items', 'acordos', 'lista', 'data'];
  const fromKeys = listKeys
    .map((key) => source?.[key] ?? record?.[key])
    .find(Array.isArray) as Record<string, unknown>[] | undefined;
  let items: Record<string, unknown>[] = fromKeys ?? [];

  if (Array.isArray(payload)) {
    items = payload as Record<string, unknown>[];
  } else if (Array.isArray(record?.data)) {
    items = record.data as Record<string, unknown>[];
  }
  const totalPages = firstNumber(
    source?.totalPages,
    source?.totalPaginas,
    record?.totalPages,
    record?.totalPaginas,
  );
  const totalItems = firstNumber(
    source?.totalElements,
    source?.totalItems,
    source?.total,
    record?.totalElements,
    record?.totalItems,
    record?.total,
  );
  let pages = items.length ? 1 : 0;

  if (totalPages && totalPages > 0) {
    pages = totalPages;
  } else if (totalItems && totalItems > 0) {
    pages = Math.ceil(totalItems / PAGE_SIZE);
  }

  return { items, totalPages: pages };
};

const toHistoryItem = (item: Record<string, unknown>): NegotiationHistoryItem => {
  const option = asRecord(item.opcaoPagamento);
  const client = asRecord(asRecord(item.consulta)?.dadosConsulta);

  return {
    idNegociacao: firstText(item.idNegociacao, item.id),
    nome: firstText(item.nome, item.nomeCliente, client?.nome),
    cpf: firstText(item.cpf, item.cpfCliente, client?.cpf).replace(/\D/g, ''),
    data: firstText(item.dataCriacao, item.data, item.dataNegociacao, item.dataAcordo),
    valorOriginal: firstNumber(option?.valorOriginal, item.valorOriginal),
    desconto: firstNumber(option?.valorDesconto, item.desconto, item.valorDesconto),
    entrada: firstNumber(
      option?.valorComDesconto,
      item.entrada,
      item.valorEntrada,
      item.valorComDesconto,
    ),
    parcelas: firstNumber(option?.parcelas, item.parcelas, item.quantidadeParcelas),
    vencimento: firstText(
      item.vencimentoPrimeiroPagamento,
      option?.vencimento,
      item.vencimento,
      item.dataVencimento,
    ),
    status: firstText(item.status),
    operador: firstText(item.nomeOperador, item.operador, item.cpfOperador),
  };
};

const DebtNegotiationHistory = (): JSX.Element => {
  const router = useHistory();
  const { user } = useAuth();
  const [form, setForm] = useState<Form>(INITIAL_FORM);
  const [list, setList] = useState<IListColCampaing[][]>([]);
  const [feedback, setFeedback] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [searchKey, setSearchKey] = useState(0);
  const [searchedCpf, setSearchedCpf] = useState('');
  const listNegotiations = useApi(
    Endpoints.debtNegotiation.listNegotiations,
    ApiMethod.POST,
  );

  const cpfDigits = form.cpf?.unmasked || '';
  const cpfIsPartial = cpfDigits.length > 0 && cpfDigits.length !== 11;

  const goToDetail = (_id: unknown, item: NegotiationHistoryItem) => {
    router.push('/negociacao-dividas/historico/detalhes', item);
  };

  const buildRow = (
    item: NegotiationHistoryItem,
    group: {
      rowSpan?: number;
      hiddenIdentity?: boolean;
      showBottomBorder?: boolean;
    },
  ): IListColCampaing[] => [
    {
      border: true,
      title: 'Nome completo',
      value: item.nome || '-',
      maxChar: 24,
      textAlign: 'left',
      rowSpan: group.hiddenIdentity ? undefined : group.rowSpan,
      hidden: group.hiddenIdentity,
    },
    {
      border: true,
      title: 'CPF',
      value: item.cpf ? cpfMask(item.cpf).masked : '-',
      maxChar: 16,
      textAlign: 'center',
      rowSpan: group.hiddenIdentity ? undefined : group.rowSpan,
      hidden: group.hiddenIdentity,
    },
    {
      title: 'Data',
      value: formatDate(item.data),
      maxChar: 12,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Valor original',
      value: formatMoney(item.valorOriginal),
      maxChar: 16,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Desconto',
      value: formatMoney(item.desconto),
      maxChar: 14,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Entrada',
      value: formatMoney(item.entrada),
      maxChar: 14,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Parcelas',
      value:
        item.parcelas === undefined || item.parcelas === null
          ? '-'
          : String(item.parcelas),
      maxChar: 10,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Vencimento',
      value: formatDate(item.vencimento),
      maxChar: 12,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Status',
      value: item.status || '-',
      maxChar: 12,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      title: 'Operador',
      value: item.operador || '-',
      maxChar: 16,
      textAlign: 'center',
      showBottomBorder: group.showBottomBorder,
    },
    {
      item,
      maxChar: 8,
      textAlign: 'center',
      searchAction: goToDetail,
      showBottomBorder: group.showBottomBorder,
    },
  ];

  const fetchHistory = async (page: number, cpf = searchedCpf) => {
    const cpfOperador = String(user?.cpf || '').replace(/\D/g, '');

    if (!cpfOperador) {
      setFeedback('Não foi possível identificar o operador.');
      return;
    }

    if ((cpf && cpf.length !== 11) || listNegotiations.loading) return;

    setFeedback('');

    try {
      const response = await listNegotiations.callApi({
        cpfOperador,
        page,
        size: PAGE_SIZE,
        ...(cpf ? { cpf } : {}),
      });

      if (!(response.status >= 200 && response.status < 300)) {
        setList([]);
        setTotalPages(0);
        setFeedback(
          response.mensagem || 'Não foi possível consultar o histórico.',
        );
        return;
      }

      const { items, totalPages: pages } = readNegotiations(response.data);
      const history = items.map(toHistoryItem);

      if (!history.length) {
        setList([]);
        setTotalPages(0);
        setFeedback('Nenhuma negociação encontrada.');
        return;
      }

      setTotalPages(pages);
      setList(
        history.map((item, index) => {
          const previous = history[index - 1];
          const next = history[index + 1];
          const sameAsPrevious = previous?.cpf && previous.cpf === item.cpf;
          const sameAsNext = next?.cpf && next.cpf === item.cpf;
          let rowSpan = 1;

          if (!sameAsPrevious) {
            while (history[index + rowSpan]?.cpf === item.cpf && item.cpf) {
              rowSpan += 1;
            }
          }

          return buildRow(item, {
            rowSpan: sameAsPrevious ? undefined : rowSpan,
            hiddenIdentity: Boolean(sameAsPrevious),
            showBottomBorder: Boolean(sameAsNext),
          });
        }),
      );
    } catch (error) {
      setList([]);
      setTotalPages(0);
      setFeedback(
        error instanceof Error
          ? error.message
          : 'Não foi possível consultar o histórico.',
      );
    }
  };

  useEffect(() => {
    fetchHistory(1, '');
  }, [user?.cpf]);

  const handleSearch = () => {
    setSearchedCpf(cpfDigits);
    setSearchKey((current) => current + 1);
    fetchHistory(1, cpfDigits);
  };

  const handleClear = () => {
    setForm({
      cpf: { name: 'cpf', value: '', unmasked: '' },
    });
    setSearchedCpf('');
    setSearchKey((current) => current + 1);
    fetchHistory(1, '');
  };

  return (
    <Container>
      <St.BoxCard>
        <ContainerFlex row space>
          <ContainerFlex padding={16}>
            <Input
              label="CPF"
              name={form.cpf.name}
              value={form.cpf.value}
              onChange={(e) => setFormState(e, form, setForm)}
              placeholder="000.000.000-00"
              maxLength={14}
              mask={MaskTypes.CPF}
            />
          </ContainerFlex>
          <ContainerFlex padding={16} row gap="12" align="flex-end">
            <Button fitContent outlined onClick={handleClear}>
              <span>Limpar pesquisa</span>
            </Button>
            <Button
              fitContent
              onClick={handleSearch}
              disabled={cpfIsPartial || listNegotiations.loading}
              loading={listNegotiations.loading}
            >
              <span>Buscar</span>
            </Button>
          </ContainerFlex>
        </ContainerFlex>
        {feedback && <St.Feedback>{feedback}</St.Feedback>}
      </St.BoxCard>

      <Loading status={listNegotiations.loading} />

      {list.length > 0 && (
        <St.BoxCard>
          <TableList customerHeader={historyHeader} list={list} />
          {totalPages > 1 && (
            <ContainerFlex row center marginTop={16}>
              <Pagination
                key={searchKey}
                totalPages={totalPages}
                onCurrentPageChange={fetchHistory}
              />
            </ContainerFlex>
          )}
        </St.BoxCard>
      )}
    </Container>
  );
};

export default DebtNegotiationHistory;
